import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { lang } from "../../../../middleware/lang";
import { useInventoryTour } from "./InventoryTourContext";
import { TOUR_STEPS } from "./tourSteps";

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;
const TOOLTIP_GAP = 12;

const TourOverlay: React.FC = () => {
  const { isActive, step, currentStep, totalSteps, nextStep, prevStep, dismissTour } =
    useInventoryTour();
  const location = useLocation();
  const [targetRect, setTargetRect] = useState<Rect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const [visible, setVisible] = useState(false);

  const measureTarget = useCallback(() => {
    if (!step || !step.target) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (el) {
      // Scroll into view first, then measure in viewport coords (overlay is position:fixed)
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    } else {
      setTargetRect(null);
    }
  }, [step]);

  // Remeasure when step changes or route changes
  useEffect(() => {
    if (!isActive || !step) {
      setVisible(false);
      return;
    }

    // Short delay to let the page render after navigation
    const timer = setTimeout(() => {
      measureTarget();
      setVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [isActive, step, currentStep, location.pathname, measureTarget]);

  // Remeasure on resize/scroll
  useEffect(() => {
    if (!isActive) return;
    const handler = () => measureTarget();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [isActive, measureTarget]);

  // Calculate tooltip position
  useEffect(() => {
    if (!step || !visible) return;

    if (step.placement === "center" || !targetRect) {
      // Center on screen
      const tooltipEl = tooltipRef.current;
      if (tooltipEl) {
        const tw = tooltipEl.offsetWidth;
        const th = tooltipEl.offsetHeight;
        setTooltipPos({
          top: window.innerHeight / 2 - th / 2,
          left: window.innerWidth / 2 - tw / 2,
        });
      }
      return;
    }

    const tooltipEl = tooltipRef.current;
    if (!tooltipEl) return;

    const tw = tooltipEl.offsetWidth;
    const th = tooltipEl.offsetHeight;
    const r = targetRect;

    let top = 0;
    let left = 0;

    switch (step.placement) {
      case "bottom":
        top = r.top + r.height + PADDING + TOOLTIP_GAP;
        left = r.left + r.width / 2 - tw / 2;
        break;
      case "top":
        top = r.top - PADDING - TOOLTIP_GAP - th;
        left = r.left + r.width / 2 - tw / 2;
        break;
      case "right":
        top = r.top + r.height / 2 - th / 2;
        left = r.left + r.width + PADDING + TOOLTIP_GAP;
        break;
      case "left":
        top = r.top + r.height / 2 - th / 2;
        left = r.left - PADDING - TOOLTIP_GAP - tw;
        break;
    }

    // Keep within viewport (fixed positioning = viewport coords)
    const maxLeft = window.innerWidth - tw - 16;
    const minLeft = 16;
    left = Math.max(minLeft, Math.min(maxLeft, left));

    const maxTop = window.innerHeight - th - 16;
    const minTop = 16;
    top = Math.max(minTop, Math.min(maxTop, top));

    setTooltipPos({ top, left });
  }, [step, targetRect, visible]);

  // Handle escape key
  useEffect(() => {
    if (!isActive) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismissTour();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isActive, dismissTour]);

  if (!isActive || !step || !visible) return null;

  const isCentered = step.placement === "center" || !targetRect;

  return (
    <div className="inventory-tour-overlay">
      {/* Backdrop */}
      {isCentered ? (
        <div className="tour-backdrop tour-backdrop-full" onClick={dismissTour} />
      ) : (
        <>
          {/* Four-piece backdrop around the target */}
          <div
            className="tour-backdrop"
            style={{
              top: 0,
              left: 0,
              width: "100vw",
              height: targetRect!.top - PADDING,
            }}
            onClick={dismissTour}
          />
          <div
            className="tour-backdrop"
            style={{
              top: targetRect!.top - PADDING,
              left: 0,
              width: targetRect!.left - PADDING,
              height: targetRect!.height + PADDING * 2,
            }}
            onClick={dismissTour}
          />
          <div
            className="tour-backdrop"
            style={{
              top: targetRect!.top - PADDING,
              left: targetRect!.left + targetRect!.width + PADDING,
              width: `calc(100vw - ${targetRect!.left + targetRect!.width + PADDING}px)`,
              height: targetRect!.height + PADDING * 2,
            }}
            onClick={dismissTour}
          />
          <div
            className="tour-backdrop"
            style={{
              top: targetRect!.top + targetRect!.height + PADDING,
              left: 0,
              width: "100vw",
              height: `calc(100vh - ${targetRect!.top + targetRect!.height + PADDING}px)`,
            }}
            onClick={dismissTour}
          />
          {/* Highlight ring */}
          <div
            className="tour-highlight"
            style={{
              top: targetRect!.top - PADDING,
              left: targetRect!.left - PADDING,
              width: targetRect!.width + PADDING * 2,
              height: targetRect!.height + PADDING * 2,
            }}
          />
        </>
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`tour-tooltip ${isCentered ? "tour-tooltip-centered" : ""}`}
        style={{
          position: "fixed",
          top: tooltipPos.top,
          left: tooltipPos.left,
        }}
      >
        {/* Close button */}
        <button
          className="tour-close"
          onClick={dismissTour}
          title={lang("Cerrar tour", "Close tour")}
        >
          &times;
        </button>

        <h5 className="tour-tooltip-title">{step.title}</h5>
        <div className="tour-tooltip-content">
          {step.content.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {line.startsWith("•") ? (
                <div className="tour-tooltip-bullet">{line}</div>
              ) : (
                <p className={line ? "" : "mb-1"}>{line}</p>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step indicator + navigation */}
        <div className="tour-footer">
          <div className="tour-dots">
            {TOUR_STEPS.map((_, i) => (
              <span
                key={i}
                className={`tour-dot ${i === currentStep ? "tour-dot-active" : ""} ${i < currentStep ? "tour-dot-done" : ""}`}
              />
            ))}
          </div>
          <div className="tour-step-counter">
            {currentStep + 1} / {totalSteps}
          </div>
        </div>

        <div className="tour-buttons">
          {currentStep > 0 && (
            <button
              className="btn btn-sm btn-outline-secondary tour-btn-prev"
              onClick={prevStep}
            >
              <i className="fa fa-chevron-left mr-1" />
              {lang("Anterior", "Previous")}
            </button>
          )}
          <button className="btn btn-sm btn-link text-muted tour-btn-skip" onClick={dismissTour}>
            {lang("Salir del tour", "Exit tour")}
          </button>
          <button className="btn btn-sm btn-primary tour-btn-next" onClick={nextStep}>
            {step.nextLabel || lang("Siguiente", "Next")}
            {!step.nextLabel && <i className="fa fa-chevron-right ml-1" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TourOverlay;
